import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useLocation, useRouter, type ErrorComponentProps } from '@tanstack/react-router';

import { NotConfiguredError } from '../api/client';
import { nowLabel, Wall } from '../components/wall';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => <Outlet />,
  errorComponent: ErrorPage,
  notFoundComponent: NotFoundPage,
});

// A page that could not load. The service's own sentence when it gave one,
// the page and the moment so the person can relay it, and the two things
// they can do. Never a stack trace.
function ErrorPage({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const path = useLocation({ select: (l) => `${l.pathname}${l.searchStr}` });
  const retry = () => { reset(); void router.invalidate(); };
  if (error instanceof NotConfiguredError && error.stage) {
    const title = error.stage === 'database' ? 'Console cannot reach its database' : error.stage === 'keys' ? 'Console cannot use its keys' : 'Console failed to start';
    return (
      <Wall
        kind="fault"
        title={title}
        what="The service behind this console failed while starting up, so no page can load right now. Nothing was changed. Send the report to MotionSafe."
        details={[{ label: 'Reason', value: error.reason ?? error.message }, { label: 'Stage', value: error.stage }, { label: 'Page', value: path }, { label: 'When', value: nowLabel() }]}
        primary={{ label: 'Try again', onClick: retry }}
        secondary={{ label: 'Go to sign-in', href: '/' }}
      />
    );
  }
  const message = error instanceof Error && error.message ? error.message : String(error);
  return (
    <Wall
      kind="fault"
      title="Page could not load"
      what="Something failed between this page and the service. Nothing you did caused it, and nothing was changed. Try again in a moment; if it keeps failing, send the report to MotionSafe."
      details={[{ label: 'Error', value: message }, { label: 'Page', value: path }, { label: 'When', value: nowLabel() }]}
      primary={{ label: 'Try again', onClick: retry }}
      secondary={{ label: 'Go to sign-in', href: '/' }}
    />
  );
}

function NotFoundPage() {
  const path = useLocation({ select: (l) => l.pathname });
  return (
    <Wall
      kind="missing"
      title="No page here"
      what={<>There is nothing at <span className="id">{path}</span>. The address may be out of date, or it was typed by hand. Everything in the console is reachable from the overview.</>}
      primary={{ label: 'Go to the overview', href: '/' }}
    />
  );
}
